/**
 * /secretariat/stats — Import CSV élèves + statistiques hebdo secrétariat.
 * MVP : affiche les stats basiques + un placeholder pour import CSV (à implémenter Phase 2).
 */
import React, { useEffect, useState } from 'react';
import { BarChart3, Upload, Users, GraduationCap, Calendar, Clock, AlertCircle } from 'lucide-react';
import api from '../../api';

export default function SecretariatStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Import & stats · Secrétariat';
    (async () => {
      try {
        const s = await api.boGetSecretariatStats();
        setStats(s);
      } catch (e) {
        setError(e.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-5xl" data-testid="secretariat-stats-page">
      <div className="mb-6">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-400 mb-1 flex items-center gap-2">
          <BarChart3 size={12} /> Secrétariat · Statistiques
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Import & pilotage secrétariat
        </h1>
        <p className="text-sm text-white/60 mt-1">
          Vue synthétique de l’activité auto-école : élèves, enseignants, leçons du jour et à venir.
        </p>
      </div>

      {loading && <div className="text-white/50 text-sm">Chargement…</div>}

      {error && (
        <div className="rounded-lg border-2 border-red-800 bg-red-950/30 p-4 text-sm text-red-200 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="secretariat-kpis">
          <StatCard label="Élèves actifs" value={stats.eleves} icon={Users} color="#1d4ed8" testid="kpi-eleves" />
          <StatCard label="Enseignants" value={stats.enseignants} icon={GraduationCap} color="#059669" testid="kpi-enseignants" />
          <StatCard label="Leçons aujourd’hui" value={stats.lessons_today} icon={Calendar} color="#dc2626" testid="kpi-lessons-today" />
          <StatCard label="À venir" value={stats.lessons_future} icon={Clock} color="#f59e0b" testid="kpi-lessons-future" />
        </div>
      )}

      <div className="rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] p-8 text-center" data-testid="import-csv-placeholder">
        <div className="w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/30 mx-auto mb-3 flex items-center justify-center">
          <Upload className="w-5 h-5 text-blue-400" />
        </div>
        <div className="text-white font-black text-lg mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Import CSV élèves
        </div>
        <p className="text-sm text-white/60 max-w-md mx-auto mb-4">
          Fonction à activer en Phase 2 : uploadez un CSV avec colonnes <code className="bg-white/10 px-1 rounded">name, email, phone, formula</code> pour créer en masse.
        </p>
        <button disabled className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/40 text-sm font-semibold cursor-not-allowed" data-testid="import-csv-btn-disabled">
          <Upload className="w-4 h-4" /> Bientôt disponible
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rounded-xl border-2 border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[10px] font-black uppercase tracking-widest text-white/50">{label}</div>
      </div>
      <div className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {value ?? '—'}
      </div>
    </div>
  );
}
